import React from 'react';
import { Search, Download, Calendar } from 'lucide-react';
export type DateRange = 'Today' | 'WTD' | '7D' | 'MTD' | '30D' | 'Custom';
export interface FilterState {
  dateRange: DateRange;
  team: string;
  cohort: string;
  roleStage: string;
  showFlaggedOnly: boolean;
  searchQuery: string;
  customStartDate: string;
  customEndDate: string;
}
export function isCountMode(dateRange: DateRange): boolean {
  return dateRange === 'Today' || dateRange === 'WTD';
}
interface DashboardHeaderProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  teams: string[];
  cohorts: string[];
  roleStages: string[];
  onExport: () => void;
  lastUpdated: string;
}
export function DashboardHeader({
  filters,
  onFilterChange,
  teams,
  cohorts,
  roleStages,
  onExport,
  lastUpdated
}: DashboardHeaderProps) {
  return <div className="bg-white border-b border-gray-200 py-4 px-6 flex flex-col gap-4 mb-6" data-id="element-80">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-81">
        <div data-id="element-82">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-83">
            Vanguard Manager Console
          </h1>
          <p className="text-sm text-gray-500 mt-1 flex items-center gap-2" data-id="element-84">
            Monitor agent performance and transfer quality
            <span className="text-gray-300" data-id="element-85">|</span>
            <span className="text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-600" data-id="element-86">
              Last updated: {lastUpdated}
            </span>
          </p>
        </div>

        <div className="flex items-center gap-3" data-id="element-87">
          <div className="relative" data-id="element-88">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" data-id="element-89" />
            <input type="text" placeholder="Search Agent..." value={filters.searchQuery} onChange={e => onFilterChange({
            ...filters,
            searchQuery: e.target.value
          })} className="pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none w-48" data-id="element-90" />
          </div>
          <button onClick={onExport} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-91">
            <Download className="w-4 h-4" data-id="element-92" />
            Export CSV
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-gray-100" data-id="element-93">
        {/* Date Range Selector */}
        <div className="flex bg-gray-100 p-1 rounded-lg" data-id="element-94">
          {(['Today', 'WTD', '7D', 'MTD', '30D', 'Custom'] as const).map(range => <button key={range} onClick={() => onFilterChange({
          ...filters,
          dateRange: range
        })} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${filters.dateRange === range ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-95">
                {range}
              </button>)}
        </div>

        {/* Custom Date Range Inputs */}
        {filters.dateRange === 'Custom' && <div className="flex items-center gap-2" data-id="element-96">
            <Calendar className="w-4 h-4 text-gray-400" data-id="element-97" />
            <input type="date" value={filters.customStartDate} onChange={e => onFilterChange({
          ...filters,
          customStartDate: e.target.value
        })} className="bg-white border border-gray-300 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 px-3 py-1.5 outline-none" data-id="element-98" />
            <span className="text-sm text-gray-400" data-id="element-99">to</span>
            <input type="date" value={filters.customEndDate} onChange={e => onFilterChange({
          ...filters,
          customEndDate: e.target.value
        })} className="bg-white border border-gray-300 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 px-3 py-1.5 outline-none" data-id="element-100" />
          </div>}

        {/* Team Filter */}
        <select value={filters.team} onChange={e => onFilterChange({
        ...filters,
        team: e.target.value
      })} className="bg-white border border-gray-300 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2 outline-none" data-id="element-101">
          <option value="All" data-id="element-102">All Teams</option>
          {teams.map(team => <option key={team} value={team} data-id="element-103">
              {team}
            </option>)}
        </select>

        {/* Cohort Filter */}
        <select value={filters.cohort} onChange={e => onFilterChange({
        ...filters,
        cohort: e.target.value
      })} className="bg-white border border-gray-300 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2 outline-none" data-id="element-104">
          <option value="All" data-id="element-105">All Cohorts</option>
          {cohorts.map(cohort => <option key={cohort} value={cohort} data-id="element-106">
              {cohort}
            </option>)}
        </select>

        {/* Role Stage Filter */}
        <select value={filters.roleStage} onChange={e => onFilterChange({
        ...filters,
        roleStage: e.target.value
      })} className="bg-white border border-gray-300 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2 outline-none" data-id="element-107">
          <option value="All" data-id="element-108">All Stages</option>
          {roleStages.map(stage => <option key={stage} value={stage} data-id="element-109">
              {stage}
            </option>)}
        </select>

        {/* Show Flagged Only Toggle */}
        <label className="flex items-center cursor-pointer ml-auto" data-id="element-110">
          <div className="relative" data-id="element-111">
            <input type="checkbox" className="sr-only" checked={filters.showFlaggedOnly} onChange={e => onFilterChange({
            ...filters,
            showFlaggedOnly: e.target.checked
          })} data-id="element-112" />
            <div className={`block w-10 h-6 rounded-full transition-colors ${filters.showFlaggedOnly ? 'bg-red-500' : 'bg-gray-300'}`} data-id="element-113"></div>
            <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${filters.showFlaggedOnly ? 'transform translate-x-4' : ''}`} data-id="element-114"></div>
          </div>
          <span className="ml-3 text-sm font-medium text-gray-700" data-id="element-115">
            Flagged Only
          </span>
        </label>
      </div>
    </div>;
}